import type { ChartData, NullableFloatArray } from "atoms/Ltool/chartAtoms";

type ChartSeriesData = ChartData["data"];

export type ChartRow = {
    index: number;
    target: number | null;
    measurement: number | null;
    measurement_corrected: number | null;
    vel: number | null;
    rpm: number | null;
};

/** 1 セルのグラフ行データ。系列は index 位置で揃え、足りない位置は null で埋める。 */
export const buildChartRows = (data: ChartSeriesData, indexScale: number): ChartRow[] => {
    const target = data.target ?? [];
    const measurement = data.measurement ?? [];
    const corrected = data.measurement_corrected ?? [];
    const vel = data.vel ?? [];
    const rpm = data.rpm ?? [];
    const length = Math.max(target.length, measurement.length, corrected.length, vel.length, rpm.length);
    return Array.from({ length }, (_, i) => ({
        index: (i + 1) * indexScale,
        target: target[i] ?? null,
        measurement: measurement[i] ?? null,
        measurement_corrected: corrected[i] ?? null,
        vel: vel[i] ?? null,
        rpm: rpm[i] ?? null,
    }));
};

/**
 * 縦軸統一（computeSharedYBounds / resolveCellYAxisRange）へ渡す系列。
 * 呼び出し側の契約に従い、<Line> で描画する系列だけを返す（vel / rpm / NG_freq は含めない）。
 * 補正後設計値を含めないと、統一 ON で点線の上側が切れる。
 */
export const chartYSeries = (data: ChartSeriesData): Array<NullableFloatArray | undefined> => [
    data.target,
    data.measurement,
    data.measurement_corrected,
];

/** 描画できる値（有限な数値）が 1 つでもあるか。空・null のみの系列は点線を描かない判定に使う。 */
export const hasSeriesValue = (series: NullableFloatArray | undefined): boolean =>
    !!series && series.some((value) => typeof value === "number" && Number.isFinite(value));

export type ThreeSigmaResolution =
    | { status: "none" }
    | { status: "uniform"; values: Record<string, number> }
    | { status: "mixed" };

/**
 * 画面右上に出す補正値の採用元を決める。three_sigma は条件（セル）ごとのレスポンスに入るため、
 * 計算済みセル（success かつ three_sigma あり）が全て同じ値のときだけ採用する（案 A）。
 * 食い違えば mixed（呼び出し側は値を出さず警告する）、計算済みセルが無ければ none。
 * 比較・結果は表示グループ（groupIds）に限る（HEV の aco など余剰キーは見ない）。
 */
export const resolveChartThreeSigma = (
    entries: Record<string, ChartData>,
    groupIds: readonly string[],
): ThreeSigmaResolution => {
    const candidates = Object.values(entries).flatMap((entry) =>
        entry?.status === "success" && entry.data?.three_sigma ? [entry.data.three_sigma] : [],
    );
    if (candidates.length === 0) return { status: "none" };

    const [first, ...others] = candidates;
    if (!others.every((sigma) => groupIds.every((groupId) => sigma[groupId] === first[groupId]))) {
        return { status: "mixed" };
    }

    const values = Object.fromEntries(
        groupIds.flatMap((groupId): [string, number][] =>
            typeof first[groupId] === "number" ? [[groupId, first[groupId]]] : [],
        ),
    );
    return Object.keys(values).length > 0 ? { status: "uniform", values } : { status: "none" };
};
